import React from "react";
import { BadgeCard } from "./BadgeCard";
import {
  PAGE_DIMS, perPage, mm,
  type AveryTemplate, type PrinterCalibration,
} from "./AveryTemplates";
import type { Attendee, Branding } from "./types";

export interface PrintSheetProps {
  template: AveryTemplate;
  attendees: Attendee[];
  branding: Branding | null;
  dpi?: number;
  showCutGuides?: boolean;
  showPageNumbers?: boolean;
  showPageBorder?: boolean;
  calibration?: PrinterCalibration;
}

/**
 * Paginated grid arranging badges on the selected sheet at exact physical
 * inches × dpi. NEVER apply a screen zoom here — printer output must be 100 %.
 */
export const PrintSheet = React.forwardRef<HTMLDivElement, PrintSheetProps>(
  (
    {
      template, attendees, branding,
      dpi = 150,
      showCutGuides = true, showPageNumbers = true, showPageBorder = false,
      calibration,
    },
    ref,
  ) => {
    const size = PAGE_DIMS[template.pageSize];
    const pageW = (template.orientation === "landscape" ? size.h : size.w) * dpi;
    const pageH = (template.orientation === "landscape" ? size.w : size.h) * dpi;
    const per = perPage(template);
    const pages: Attendee[][] = [];
    for (let i = 0; i < attendees.length; i += per) {
      pages.push(attendees.slice(i, i + per));
    }
    if (pages.length === 0) pages.push([]);

    // Calibration transforms — applied uniformly to every badge on every page.
    const cal = calibration ?? { scaleX: 1, scaleY: 1, offsetX: 0, offsetY: 0, printer: "Default", updatedAt: 0 };
    const offsetXpx = mm(cal.offsetX) * dpi;
    const offsetYpx = mm(cal.offsetY) * dpi;

    return (
      <div ref={ref} className="print-sheet-root" style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        {pages.map((pageAttendees, pageIdx) => (
          <div
            key={pageIdx}
            className="print-sheet-page"
            data-page={pageIdx + 1}
            style={{
              width: pageW,
              height: pageH,
              background: "#FFFFFF",
              position: "relative",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              margin: "0 auto",
              breakAfter: "page",
              pageBreakAfter: "always",
              outline: showPageBorder ? "1px solid #94A3B8" : "none",
              outlineOffset: "-1px",
            }}
          >
            {Array.from({ length: template.rows }).flatMap((_, r) =>
              Array.from({ length: template.cols }).map((_, c) => {
                const idx = r * template.cols + c;
                const a = pageAttendees[idx];
                const badgeWpx = template.badgeW * dpi * cal.scaleX;
                const badgeHpx = template.badgeH * dpi * cal.scaleY;
                const x = (template.marginLeft + c * (template.badgeW + template.colGap)) * dpi + offsetXpx;
                const y = (template.marginTop + r * (template.badgeH + template.rowGap)) * dpi + offsetYpx;
                return (
                  <div
                    key={`${r}-${c}`}
                    style={{
                      position: "absolute",
                      left: x,
                      top: y,
                      width: badgeWpx,
                      height: badgeHpx,
                    }}
                  >
                    {a ? (
                      <BadgeCard
                        attendee={a}
                        branding={branding}
                        widthIn={template.badgeW * cal.scaleX}
                        heightIn={template.badgeH * cal.scaleY}
                        dpi={dpi}
                      />
                    ) : null}
                  </div>
                );
              }),
            )}
            {showCutGuides && (
              <CutGuides
                template={template}
                dpi={dpi}
                pageW={pageW}
                pageH={pageH}
                offsetXpx={offsetXpx}
                offsetYpx={offsetYpx}
                cal={cal}
              />
            )}
            {showPageNumbers && (
              <div
                style={{
                  position: "absolute",
                  bottom: 6,
                  right: 12,
                  fontSize: 10,
                  color: "#94A3B8",
                  fontFamily: "monospace",
                }}
              >
                Page {pageIdx + 1} of {pages.length} · 100 % scale · {template.pageSize.toUpperCase()} {template.orientation}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  },
);
PrintSheet.displayName = "PrintSheet";

/** Thin gray crop marks drawn in the *gaps* between badges — never overlapping
 *  a badge. Marks are 6 mm long, positioned in the middle of each gap. */
function CutGuides({
  template, dpi, pageW, pageH, offsetXpx, offsetYpx, cal,
}: {
  template: AveryTemplate;
  dpi: number;
  pageW: number;
  pageH: number;
  offsetXpx: number;
  offsetYpx: number;
  cal: PrinterCalibration;
}) {
  const stroke = "#CBD5E1";
  const strokeWidth = 0.5;
  const markLen = mm(6) * dpi;
  const lines: React.ReactElement[] = [];

  const badgeW = template.badgeW * dpi * cal.scaleX;
  const badgeH = template.badgeH * dpi * cal.scaleY;
  const colGap = template.colGap * dpi;
  const rowGap = template.rowGap * dpi;
  const originX = template.marginLeft * dpi + offsetXpx;
  const originY = template.marginTop * dpi + offsetYpx;

  // Vertical guides — at each badge column edge (top & bottom of the page).
  for (let c = 0; c <= template.cols; c++) {
    const x = originX + c * (badgeW + colGap) - (c === 0 ? 0 : colGap / 2);
    lines.push(<line key={`v-top-${c}`} x1={x} x2={x} y1={0} y2={markLen} stroke={stroke} strokeWidth={strokeWidth} />);
    lines.push(<line key={`v-bot-${c}`} x1={x} x2={x} y1={pageH - markLen} y2={pageH} stroke={stroke} strokeWidth={strokeWidth} />);
  }
  // Horizontal guides — at each badge row edge (left & right of the page).
  for (let r = 0; r <= template.rows; r++) {
    const y = originY + r * (badgeH + rowGap) - (r === 0 ? 0 : rowGap / 2);
    lines.push(<line key={`h-l-${r}`} x1={0} x2={markLen} y1={y} y2={y} stroke={stroke} strokeWidth={strokeWidth} />);
    lines.push(<line key={`h-r-${r}`} x1={pageW - markLen} x2={pageW} y1={y} y2={y} stroke={stroke} strokeWidth={strokeWidth} />);
  }

  return (
    <svg
      width={pageW}
      height={pageH}
      style={{ position: "absolute", left: 0, top: 0, pointerEvents: "none" }}
      viewBox={`0 0 ${pageW} ${pageH}`}
    >
      {lines}
    </svg>
  );
}
